import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// 3D Design Item Component
const ThreeDDesignItem: React.FC<{
  image: string;
  index: number;
}> = ({ image, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative overflow-hidden rounded-xl transition-all duration-500 hover:shadow-2xl hover:shadow-brand-orange/20 hover:-translate-y-2 ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img 
          src={image} 
          alt="3D Design"
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
        />

        {/* 3D Floating Elements */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:animate-bounce">
          <span className="text-white text-sm">3D</span>
        </div>
        <div className="absolute top-4 left-4 w-6 h-6 bg-brand-orange/80 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:animate-pulse"></div>
      </div>
    </div>
  );
};

const ThreeDDesignsPage: React.FC = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.3);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [statsRef, statsVisible] = useScrollAnimation(0.4);

  // 3D Design images from the folder - All new images
  const threeDDesigns = [
    "/Our Creative 3D Designs/0017adba-ece5-4d9d-b1fa-2e1389c35016.jpg",
    "/Our Creative 3D Designs/001f5dfb-5444-4e92-b803-7915e5ae007a.jpg",
    "/Our Creative 3D Designs/00d20109-9f82-4e1e-a9aa-d5d51d329952.jpg",
    "/Our Creative 3D Designs/019c8aa8-4267-4512-86cd-02ffe6476656.jpg",
    "/Our Creative 3D Designs/01ec3118-4b3b-4eae-a00d-ec4688694691.jpg",
    "/Our Creative 3D Designs/061ea087-f683-450a-9f28-973f60f2aa32.jpg",
    "/Our Creative 3D Designs/07a6b2d5-f85d-421b-a849-7f4c5a207f74.jpg",
    "/Our Creative 3D Designs/0f49e5b9-0d3f-4c0a-a9f2-ace36fcd99a2.jpg",
    "/Our Creative 3D Designs/122680c5-d984-4bce-b675-ee4b6fc64a9b.jpg",
    "/Our Creative 3D Designs/1276e597-986f-46e0-a0f3-21cfd4bc88da.jpg",
    "/Our Creative 3D Designs/14b691bc-2748-4111-b720-1935de952dad.jpg",
    "/Our Creative 3D Designs/16ab2c0a-65ae-47cf-bd31-221d03ba4e03.jpg",
    "/Our Creative 3D Designs/1da57f47-64ac-41a4-991b-503aa5a67643.jpg",
    "/Our Creative 3D Designs/1e521e20-ccb0-4375-8783-590366dea967.jpg",
    "/Our Creative 3D Designs/20bb0e0f-a440-42a5-8474-e97b0dc805e2.jpg",
    "/Our Creative 3D Designs/23f0ebc0-e142-45f4-809b-4f370262edcd.jpg",
    "/Our Creative 3D Designs/287c2de0-6613-4567-bcc9-4f7920e0bd11.jpg",
    "/Our Creative 3D Designs/289ca551-7dc7-4af3-8792-cdbdfa450a73.jpg",
    "/Our Creative 3D Designs/28c83108-5d72-481a-99ce-6c58fe672f1b.jpg",
    "/Our Creative 3D Designs/33445203-c4d8-4e64-a926-dc3fcfab0832.jpg",
    "/Our Creative 3D Designs/3698f177-f854-4c4f-b61f-68e997b98555.jpg",
    "/Our Creative 3D Designs/3aad6e31-e653-49b3-a712-72af084d88e5.jpg",
    "/Our Creative 3D Designs/3ada4644-a6ea-437b-85d9-5dcd216aab87.jpg",
    "/Our Creative 3D Designs/3fd56c96-3e73-4806-8764-0195abe27459.jpg",
    "/Our Creative 3D Designs/41506fb6-5afc-4dfd-ac06-502569ad618e.jpg",
    "/Our Creative 3D Designs/4812ddd0-a342-402b-9f90-22d69bf585a7.jpg",
    "/Our Creative 3D Designs/4fd9370c-999f-45cd-aa1d-40339571d45c.jpg",
    "/Our Creative 3D Designs/505f077f-25eb-4771-b2da-3866f325b1db.jpg",
    "/Our Creative 3D Designs/559ad1b2-9bbf-4888-8f32-e83e152af6da.jpg",
    "/Our Creative 3D Designs/5b1ed113-9305-4c13-9d51-da73a6396343.jpg",
    "/Our Creative 3D Designs/5bd280cc-a8a7-440b-aa6e-b845df97e176.jpg",
    "/Our Creative 3D Designs/60194b2a-c254-4805-8461-842ab92b6122.jpg",
    "/Our Creative 3D Designs/650fbe97-83bd-4f81-9dcb-649c6c5f84b3.jpg",
    "/Our Creative 3D Designs/6755ad06-f494-49b3-8029-aa2ea5a56992.jpg",
    "/Our Creative 3D Designs/733537c9-94eb-4136-b2af-56ff2acfaf8d.jpg",
    "/Our Creative 3D Designs/74df2dd8-3474-4cac-bd09-b962983675b6.jpg",
    "/Our Creative 3D Designs/758b413f-43f8-4c25-a0c0-3553b777efaa.jpg",
    "/Our Creative 3D Designs/75973fb9-196d-4869-98ee-06ff78e658aa.jpg",
    "/Our Creative 3D Designs/784d4149-42f1-4daa-9f25-90ab73ff3166.jpg",
    "/Our Creative 3D Designs/7a125bf0-b0db-483d-9d97-f3fc751363c6.jpg",
    "/Our Creative 3D Designs/7d9a551d-0be6-44f2-81bb-477cfe1891c4.jpg",
    "/Our Creative 3D Designs/7da0467d-29f4-4329-9006-f1e0ea1b68d7.jpg",
    "/Our Creative 3D Designs/7dda82b9-c394-46b4-bed5-a27a5d706bbf.jpg",
    "/Our Creative 3D Designs/81e0f2ad-339f-4141-922c-1db55b356a87.jpg",
    "/Our Creative 3D Designs/82ba126d-0e62-4c7e-8ebe-0fae053e46f3.jpg",
    "/Our Creative 3D Designs/86ab68cb-5654-4e3d-97e7-f87b422c5f41.jpg",
    "/Our Creative 3D Designs/88891e5a-587d-4ae9-a87c-a67fad7c959e.jpg",
    "/Our Creative 3D Designs/890aaf22-607d-416d-a7b3-15ac0e0cdbf6.jpg",
    "/Our Creative 3D Designs/8b60de31-dc49-4c35-9829-f7b7d2579f91.jpg",
    "/Our Creative 3D Designs/8f95ce71-f26b-4c65-b33f-9e74a7d7b2d7.jpg",
    "/Our Creative 3D Designs/9053c562-77a8-4f32-aea3-c6f0d7370aed.jpg",
    "/Our Creative 3D Designs/908f0de2-6eca-4df2-a721-0d1bd919e5a6.jpg",
    "/Our Creative 3D Designs/90f60c73-ac86-451b-b035-a4d473127e93.jpg",
    "/Our Creative 3D Designs/97156274-e3b0-46a2-b17d-27afe55aba3a.jpg",
    "/Our Creative 3D Designs/99f5c984-e037-4533-889b-e59a75e3972f.jpg",
    "/Our Creative 3D Designs/9c949cf1-3878-47c9-b3e5-90d8b212cb36.jpg",
    "/Our Creative 3D Designs/9cb34503-e96c-45bb-86a3-bf8024234d15.jpg",
    "/Our Creative 3D Designs/9d060685-fe49-4c46-bae6-e3ff9902dbfb.jpg",
    "/Our Creative 3D Designs/9f0eecfd-4850-49eb-8756-c9442add1a95.jpg",
    "/Our Creative 3D Designs/a4827988-605d-4f37-815a-1f3bd39ad1d9.jpg",
    "/Our Creative 3D Designs/a5f197df-c2a9-4593-a31c-0b3294903c32.jpg",
    "/Our Creative 3D Designs/a6701a1f-6922-4989-b6be-2825d3f6ff7b.jpg",
    "/Our Creative 3D Designs/acb2851b-41d7-491d-9b2c-206c24c4269e.jpg",
    "/Our Creative 3D Designs/acc68637-815b-4456-b649-554a979d58cd.jpg",
    "/Our Creative 3D Designs/ad158e72-2582-418b-84c1-467a2c45e3c2.jpg",
    "/Our Creative 3D Designs/af0a7a64-fffe-43eb-9be7-77e05d5d0523.jpg",
    "/Our Creative 3D Designs/af2bcb11-b383-4e31-964d-f0e2f276c216.jpg",
    "/Our Creative 3D Designs/b046e30c-ec22-4196-9cce-44bf139f2675.jpg",
    "/Our Creative 3D Designs/b3acd267-4f7e-4d27-980a-50d2e5604e1d.jpg",
    "/Our Creative 3D Designs/b66030f5-c402-4f96-802e-c5e9ca47a5fc.jpg",
    "/Our Creative 3D Designs/b69e53db-b8b2-43af-8ad5-a80279706399.jpg",
    "/Our Creative 3D Designs/b844b952-6107-4a3a-a6ba-e2c21ebc7544.jpg",
    "/Our Creative 3D Designs/b9e20aa0-58f2-45fd-a1e7-0ee9edfeea7c.jpg",
    "/Our Creative 3D Designs/bbf3899f-d1dc-437e-b603-392312f21845.jpg",
    "/Our Creative 3D Designs/c0d53951-708c-47fa-86f0-c82077b63e02.jpg",
    "/Our Creative 3D Designs/c4ea702d-c460-409a-bc1d-d867c66c7627.jpg",
    "/Our Creative 3D Designs/d6643cfe-317f-41ca-9d2a-6b337e252134.jpg",
    "/Our Creative 3D Designs/e2355b74-e80d-4ca4-b2f1-7d71be3d6ecc.jpg",
    "/Our Creative 3D Designs/e5fa9976-5fc2-4be1-b837-45887019c06e.jpg",
    "/Our Creative 3D Designs/e763e971-556b-4624-a94e-41f9495545d4.jpg",
    "/Our Creative 3D Designs/e922a326-f3b9-4d02-835e-c4408e272403.jpg",
    "/Our Creative 3D Designs/ee3dddec-515f-43d0-95ed-c21724e75c0b.jpg",
    "/Our Creative 3D Designs/fe792c67-1ddc-4250-9bb4-5985aa6ec2cd.jpg",
    "/Our Creative 3D Designs/fe85df7c-a76c-4294-b534-b4eb42437bb3.jpg",
    "/Our Creative 3D Designs/ff29fa02-186f-403e-8db0-6ac588a57d71.jpg"
  ];

  const stats = [
    { number: '85+', label: '3D Designs', icon: '🎨' },
    { number: '20+', label: 'Categories', icon: '📂' },
    { number: '100%', label: 'Custom Solutions', icon: '✨' },
    { number: '24/7', label: 'Support', icon: '🚀' }
  ];

  return (
    <div className="pt-32 min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 via-red-600/10 to-yellow-600/10"></div>
          <div className="absolute top-20 left-20 w-32 h-32 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            ref={headerRef}
            className={`text-center transition-all duration-1000 ${
              headerVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium mb-6">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span>Creative 3D Solutions</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                Our Creative
              </span>
              <br />
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                3D Designs
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Explore our extensive collection of innovative 3D designs, creative solutions, and cutting-edge visual experiences that bring brands to life.
            </p>

            {/* Stats */}
            <div 
              ref={statsRef}
              className={`grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto transition-all duration-1000 ${
                statsVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-sm sm:text-base text-white/80">{stat.label}</div>
                  <div className="text-2xl mt-2">{stat.icon}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3D Designs Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-gray-900 via-brand-primary to-gray-900 bg-clip-text text-transparent">
                3D Design Gallery
              </span>
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto mb-4"></div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From immersive airport experiences to interactive displays, discover how our 3D designs create memorable brand experiences.
            </p>
          </div>

          {/* 3D Designs Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {threeDDesigns.map((image, index) => (
              <ThreeDDesignItem 
                key={index} 
                image={image} 
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-primary to-brand-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Bring Your Vision to Life?
          </h3>
          <p className="text-xl text-white/90 mb-8">
            Let's discuss your 3D design project and create something extraordinary together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/quote"
              className="bg-white text-brand-orange px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Get Quote</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-brand-orange transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Back to Home</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThreeDDesignsPage;
